import React, { useState, useEffect, useMemo, useRef } from "react";
import billingPolicy from '../config/billingPolicy.json';
// Update imports to point to parent directory
import { getAllUsers, updateUserById, updateUserSettings, deleteData, getDocuments, getDocument, getRecentLoginFailures } from "../utils/firestoreService";
import { getAdminUserDocumentActivity } from "../utils/statsService";
import { resolveFeatureFlag } from "../utils/featureFlags";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Search, EyeOff, Eye, Edit, Trash2, Copy, MailCheck, ShieldCheck, CreditCard, MailX, X, UserPlus, ExternalLink, AlertTriangle, FileText } from "lucide-react";
import Chip from "@mui/material/Chip";
// Update import to point to parent directory
import { documentsPerPage } from "../globals";
import { ROLE_HIERARCHY, ROLE_LABELS, getEffectiveInviteCap } from "../utils/roleUtils";
import { flagsByKey } from "../config/featureFlagDefinitions";
import InviteModal from "../utils/InviteModal";
import { sortByDisplayLabel } from "./sortByDisplayLabel";
import SearchableAdminSelect from "./components/SearchableAdminSelect";
import { useUnsavedChanges } from './useUnsavedChanges';
import { useAdminSessionState } from './useAdminSessionState';
import { useAdminFeedback } from './useAdminFeedback';

const getAccessPresentation = (access) => {
  switch (access?.reason) {
    case 'user':
      return { label: 'On', color: 'success', severity: 'success', message: 'Effective access is on.' };
    case 'user-missing':
      return { label: 'Missing', color: 'warning', severity: 'warning', message: 'Effective access is off because this user has no saved AI Assistant preference.' };
    case 'user-off':
      return { label: 'Off', color: 'default', severity: 'info', message: 'Effective access is off because this user explicitly opted out.' };
    case 'role':
      return {
        label: 'Role blocked',
        color: 'warning',
        severity: 'warning',
        message: `Effective access is off because this feature requires ${ROLE_LABELS[access.minRole] || access.minRole || 'a higher role'}.`,
      };
    case 'company':
      return { label: 'Company off', color: 'default', severity: 'info', message: 'Effective access is off at the company gate.' };
    case 'system':
      return { label: 'System off', color: 'default', severity: 'info', message: 'Effective access is off at the system gate.' };
    default:
      return { label: 'Unknown', color: 'default', severity: 'warning', message: 'Effective access could not be resolved.' };
  }
};

const AdminUsers = ({ currentUser, adminPrefs, setAdminPrefs }) => {
  const { success: notifySuccess, error: notifyError, copy: copyValue } = useAdminFeedback();
  const userId = currentUser?.uid;
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [systemConfig, setSystemConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useAdminSessionState(`admin:${userId}:usersSearch`, '');
  const [updating, setUpdating] = useState({});
  const [currentPage, setCurrentPage] = useAdminSessionState(`admin:${userId}:usersPage`, 1);
  const [showInactive, setShowInactive] = useState(adminPrefs?.usersShowInactive ?? true);
  const [sortConfig, setSortConfig] = useState({ 
    key: !billingPolicy.enabled && ['subscriptionStatus', 'stripePlan'].includes(adminPrefs?.usersSortKey) ? 'displayName' : (adminPrefs?.usersSortKey ?? 'displayName'),
    direction: adminPrefs?.usersSortDirection ?? 'asc' 
  });
  
  // Enhanced filter states
  const [roleFilter, setRoleFilter] = useState(adminPrefs?.usersRoleFilter ?? 'all'); // 'all', 'standard', 'manager', 'superAdmin'
  const [billingFilter, setBillingFilter] = useState(billingPolicy.enabled ? (adminPrefs?.usersBillingFilter ?? 'all') : 'all'); // 'all', 'active', 'past_due', 'canceled', 'trialing', 'none'
  const [companyFilter, setCompanyFilter] = useState(adminPrefs?.usersCompanyFilter ?? 'all'); // 'all', companyId, or 'none'
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(adminPrefs?.usersShowUnverifiedOnly ?? false);
  const [showLoginIssuesOnly, setShowLoginIssuesOnly] = useState(adminPrefs?.usersShowLoginIssuesOnly ?? false);
  const [loginIssues, setLoginIssues] = useState({});
  const [userDocumentActivity, setUserDocumentActivity] = useState(null);
  
  // User settings modal state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [userFormData, setUserFormData] = useState({
    displayName: "",
    excludeFromAuditLogs: false,
    companyId: "",
    inviteCap: "",
    useAiAssistant: null,
  });
  const [initialUserFormData, setInitialUserFormData] = useState(null);
  const hasUnsavedUserChanges = settingsDialogOpen && initialUserFormData !== null
    && JSON.stringify(userFormData) !== initialUserFormData;
  const confirmDiscardUserChanges = useUnsavedChanges(hasUnsavedUserChanges);
  const openWithUserFormData = next => {
    setUserFormData(next);
    setInitialUserFormData(JSON.stringify(next));
  };

  // Invite modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allUsers, companiesData, loginFailures, sysConfig, activityResult] = await Promise.all([
          getAllUsers(),
          getDocuments('companies'),
          getRecentLoginFailures(7),
          getDocument('globalSettings', 'systemConfig'),
          getAdminUserDocumentActivity()
            .then((data) => ({ data, failed: false }))
            .catch((error) => {
              console.error("Error fetching weekly user document activity:", error);
              return { data: null, failed: true };
            }),
        ]);
        setUsers(allUsers);
        setCompanies(sortByDisplayLabel(companiesData, company => company.name || company.code || company.id));
        setSystemConfig(sysConfig);
        setUserDocumentActivity(activityResult.failed ? null : activityResult.data);

        // Build login issues lookup by email
        const issuesMap = {};
        loginFailures.forEach(entry => {
          const email = entry.userEmail || entry.metadata?.userEmail;
          if (!email) return;
          if (!issuesMap[email]) {
            issuesMap[email] = { count: 0, lastFailure: null, hasLockout: false };
          }
          issuesMap[email].count++;
          const ts = entry.timestamp?.toDate ? entry.timestamp.toDate() : (entry.timestamp ? new Date(entry.timestamp) : null);
          if (ts && (!issuesMap[email].lastFailure || ts > issuesMap[email].lastFailure)) {
            issuesMap[email].lastFailure = ts;
          }
          const code = entry.metadata?.errorCode;
          if (code === 'auth/too-many-requests') issuesMap[email].hasLockout = true;
        });
        setLoginIssues(issuesMap);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setIsLoading(false);
    };

    fetchData();
  }, []);

  // Full company docs by ID for feature access resolution.
  const companyById = useMemo(() => {
    const lookup = {};
    companies.forEach(c => { lookup[c.id] = c; });
    return lookup;
  }, [companies]);

  // Canonical effective-access trace for user-toggle features. This mirrors the
  // full system + company + role + personal-preference path in FeatureFlagContext.
  const getUserFeatureAccess = (user, flagKey) => {
    const companyDoc = user.companyId ? companyById[user.companyId] : null;
    const definition = flagsByKey[flagKey];
    const effectiveMinRole = companyDoc?.flagMinRoles?.[flagKey] !== undefined
      ? companyDoc.flagMinRoles[flagKey]
      : definition?.minRole;
    return {
      ...resolveFeatureFlag(flagKey, {
        systemConfig,
        companyDoc,
        userSettings: user.settings || {},
        userSettingKey: definition?.userSettingKey,
        userRole: user.userRole || 'standard',
        minRole: effectiveMinRole,
      }),
      minRole: effectiveMinRole,
    };
  };

  const getUserAiAccess = (user) => getUserFeatureAccess(user, 'aiAssistant');

  // Reset for a changed filter, not when restoring the same view on mount.
  const filterSignature = JSON.stringify([searchQuery, roleFilter, billingFilter, companyFilter, showInactive, showUnverifiedOnly, showLoginIssuesOnly]);
  const previousFilters = useRef(filterSignature);
  useEffect(() => {
    if (previousFilters.current !== filterSignature) {
      previousFilters.current = filterSignature;
      setCurrentPage(1);
    }
  }, [filterSignature, setCurrentPage]);

  const handleRequestSort = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortConfig({ key: property, direction: newDirection });
    // Persist to user settings
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, usersSortKey: property, usersSortDirection: newDirection };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  const handleActiveToggle = async (user) => {
    if (user.id === userId) return; // Prevent self-deactivation
    
    setUpdating(prev => ({ ...prev, [user.id]: true }));
    try {
      const newDisabledStatus = !user.disabled;
      await updateUserById(user.id, { disabled: newDisabledStatus });
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, disabled: newDisabledStatus } : u
      ));
      notifySuccess(newDisabledStatus ? 'User deactivated.' : 'User activated.');
    } catch (error) {
      console.error("Error updating user active status:", error);
      notifyError('Could not update user status. Check your connection and try again.');
    }
    setUpdating(prev => ({ ...prev, [user.id]: false }));
  };

  const handleRoleChange = async (user, newRole) => {
    if (user.id === userId && newRole !== "superAdmin") return; // Prevent self-demotion
    
    setUpdating(prev => ({ ...prev, [user.id]: true }));
    try {
      await updateUserById(user.id, { userRole: newRole });
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, userRole: newRole } : u
      ));
      notifySuccess('User role saved.');
    } catch (error) {
      console.error("Error updating user role:", error);
      notifyError('Could not save the user role. Check your connection and try again.');
    }
    setUpdating(prev => ({ ...prev, [user.id]: false }));
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleOpenSettingsDialog = (user) => {
    setSaveError('');
    setEditingUser(user);
    openWithUserFormData({
      displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '',
      excludeFromAuditLogs: user.excludeFromAuditLogs || false,
      companyId: user.companyId || "",
      // Preserve a missing value when an admin edits unrelated fields. The
      // first switch interaction turns this into an explicit true/false value.
      useAiAssistant: user.settings?.useAiAssistant === undefined
        ? null
        : user.settings.useAiAssistant === true,
      inviteCap: user.inviteCap != null ? String(user.inviteCap) : "",
    });
    setSettingsDialogOpen(true);
  };

  const handleCloseSettingsDialog = (force = false) => {
    if (!force && !confirmDiscardUserChanges()) return;
    setSettingsDialogOpen(false);
    setEditingUser(null);
  };

  const handleOpenDeleteDialog = (user) => {
    setDeleteError('');
    setUserToDelete(user);
    setDeleteConfirmText("");
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDeleteConfirmText("");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleteError('');
    setIsDeleting(true);
    try {
      await deleteData('users', userToDelete.id);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      handleCloseDeleteDialog();
      notifySuccess('User record deleted.');
    } catch (error) {
      console.error("Error deleting user:", error);
      setDeleteError('Could not delete the user record. Check your connection and try again.');
    }
    setIsDeleting(false);
  };

  const handleSaveUserSettings = async () => {
    if (!editingUser) return;

    setSaveError('');
    setIsSaving(true);
    let profileSaved = false;
    try {
      const updates = {
        displayName: userFormData.displayName,
        excludeFromAuditLogs: userFormData.excludeFromAuditLogs,
        companyId: userFormData.companyId || null,
      };
      // Only set inviteCap if a value was entered; clear it if empty
      if (userFormData.inviteCap !== "") {
        updates.inviteCap = parseInt(userFormData.inviteCap, 10) || 0;
      } else {
        updates.inviteCap = null;
      }
      await updateUserById(editingUser.id, updates);
      profileSaved = true;

      // Save user-layer feature preferences. Do not convert a legacy missing AI
      // value into an explicit opt-out when an admin edits an unrelated field.
      const featureSettings = {};
      if (userFormData.useAiAssistant !== null) {
        featureSettings.useAiAssistant = userFormData.useAiAssistant;
      }
      await updateUserSettings(editingUser.id, featureSettings);

      setUsers(prev => prev.map(u =>
        u.id === editingUser.id ? { ...u, ...updates, settings: { ...u.settings, ...featureSettings } } : u
      ));
      handleCloseSettingsDialog(true);
      notifySuccess('User settings saved.');
    } catch (error) {
      console.error("Error saving user settings:", error);
      setSaveError(profileSaved
        ? 'The user profile was saved, but preferences could not be saved. Your edits are still here; retry Save.'
        : 'Could not save user settings. Your edits are still here. Check your connection and retry Save.');
    }
    setIsSaving(false);
  };

  const handleManualVerification = async (user) => {
    setUpdating(prev => ({ ...prev, [user.id]: true }));
    try {
      const updates = {
        emailVerified: true,
        manuallyVerifiedBy: userId,
        manuallyVerifiedAt: new Date().toISOString()
      };
      await updateUserById(user.id, updates);
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, ...updates } : u
      ));
      notifySuccess('User email marked as verified.');
    } catch (error) {
      console.error("Error verifying user:", error);
      notifyError('Could not verify the user. Check your connection and try again.');
    }
    setUpdating(prev => ({ ...prev, [user.id]: false }));
  };

  const filteredUsers = users.filter(user => {
    // Filter by active status (disabled = inactive)
    if (!showInactive && user.disabled) return false;
    
    // Filter by role
    if (roleFilter !== 'all') {
      const userRole = user.userRole || 'standard';
      if (userRole !== roleFilter) return false;
    }
    
    // Filter by billing status
    if (billingPolicy.enabled && billingFilter !== 'all') {
      if (billingFilter === 'none') {
        if (user.stripeCustomerId) return false;
      } else {
        if (!user.stripeCustomerId) return false;
        if (user.subscriptionStatus !== billingFilter) return false;
      }
    }
    
    // Filter by company
    if (companyFilter !== 'all') {
      if (companyFilter === 'none') {
        if (user.companyId) return false;
      } else {
        if (user.companyId !== companyFilter) return false;
      }
    }
    
    // Filter by email verification
    if (showUnverifiedOnly && user.emailVerified !== false) return false;
    
    // Filter by login issues
    if (showLoginIssuesOnly && !loginIssues[user.email]) return false;
    
    // Filter by search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.displayName?.toLowerCase().includes(query) ||
      user.firstName?.toLowerCase().includes(query) ||
      user.lastName?.toLowerCase().includes(query)
    );
  });

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'displayName':
          aValue = (a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Unnamed').toLowerCase();
          bValue = (b.displayName || `${b.firstName || ''} ${b.lastName || ''}`.trim() || 'Unnamed').toLowerCase();
          break;
        case 'active':
          aValue = !a.disabled;
          bValue = !b.disabled;
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'userRole':
          aValue = (a.userRole || 'standard').toLowerCase();
          bValue = (b.userRole || 'standard').toLowerCase();
          break;
        case 'invites':
          aValue = a.invitesSent || 0;
          bValue = b.invitesSent || 0;
          break;
        case 'lastLoginTime':
            aValue = a.lastLoginTime?.toDate ? a.lastLoginTime.toDate().getTime() : (new Date(a.lastLoginTime || 0).getTime());
            bValue = b.lastLoginTime?.toDate ? b.lastLoginTime.toDate().getTime() : (new Date(b.lastLoginTime || 0).getTime());
            break;
        case 'subscriptionStatus':
          aValue = a.stripeCustomerId ? (a.subscriptionStatus || 'zzz') : 'zzz';
          bValue = b.stripeCustomerId ? (b.subscriptionStatus || 'zzz') : 'zzz';
          break;
        case 'stripePlan':
          aValue = a.stripeCustomerId ? (a.stripePlan || 'zzz') : 'zzz';
          bValue = b.stripeCustomerId ? (b.stripePlan || 'zzz') : 'zzz';
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredUsers, sortConfig]);

  // Pagination calculations
  const indexOfLastUser = currentPage * documentsPerPage;
  const indexOfFirstUser = indexOfLastUser - documentsPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(sortedUsers.length / documentsPerPage);

  useEffect(() => {
    if (isLoading) return;
    setCurrentPage(page => Math.min(page, Math.max(1, totalPages)));
  }, [isLoading, totalPages, setCurrentPage]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "Never";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  const inactiveCount = users.filter(u => u.disabled).length;

  const getSubscriptionStatusStyle = (status) => {
    switch (status) {
      case 'active':
        return { color: 'var(--color-success-text)', fontWeight: 500 };
      case 'past_due':
        return { color: 'var(--color-warning-text)', fontWeight: 500 };
      case 'canceled':
      case 'unpaid':
        return { color: 'var(--color-error-text)', fontWeight: 500 };
      case 'trialing':
        return { color: 'var(--color-info-text)', fontWeight: 500 };
      default:
        return { color: 'var(--color-text-secondary)' };
    }
  };

  const formatSubscriptionStatus = (user) => {
    if (!user.stripeCustomerId) return '—';
    return user.subscriptionStatus || 'unknown';
  };

  const formatPlan = (user) => {
    if (!user.stripeCustomerId) return '—';
    return user.stripePlan || 'unknown';
  };

  // Helper to persist filter preferences
  const updateFilterPref = (key, value) => {
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, [key]: value };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  // Count active filters for indicator
  const activeFilterCount = [
    !showInactive,
    roleFilter !== 'all',
    billingPolicy.enabled && billingFilter !== 'all',
    companyFilter !== 'all',
    showUnverifiedOnly,
    showLoginIssuesOnly
  ].filter(Boolean).length;

  // Clear all filters
  const clearAllFilters = () => {
    setShowInactive(true);
    setRoleFilter('all');
    setBillingFilter('all');
    setCompanyFilter('all');
    setShowUnverifiedOnly(false);
    setShowLoginIssuesOnly(false);
    if (userId && setAdminPrefs) {
      const updatedPrefs = { 
        ...adminPrefs, 
        usersShowInactive: true,
        usersRoleFilter: 'all',
        usersBillingFilter: 'all',
        usersCompanyFilter: 'all',
        usersShowUnverifiedOnly: false,
        usersShowLoginIssuesOnly: false
      };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  // Styled filter button helper
  const FilterIconButton = ({ active, onClick, tooltip, color = 'var(--color-info-text)', children }) => (
    <Tooltip title={tooltip} arrow>
      <IconButton 
        onClick={onClick}
        size="small"
        sx={{ 
          color: active ? color : 'var(--color-text-secondary)',
          backgroundColor: active ? `${color}15` : 'transparent',
          '&:hover': {
            backgroundColor: active ? `${color}25` : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );

  const editingAiSettings = editingUser
    ? {
        ...(editingUser.settings || {}),
        ...(userFormData.useAiAssistant === null
          ? {}
          : { useAiAssistant: userFormData.useAiAssistant }),
      }
    : {};
  const editingAiAccess = editingUser
    ? getUserAiAccess({
        ...editingUser,
        companyId: userFormData.companyId || null,
        settings: editingAiSettings,
      })
    : null;
  const editingAiPresentation = getAccessPresentation(editingAiAccess);

  return (
    <div className="standard-container" style={{ marginBottom: '60px'}}>
      {/* Header Row: Search + Filter Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        {/* Search */}
        <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
          <Tooltip title="Search users" arrow>
            <Search className="header-search-icon" size={18} />
          </Tooltip>
          <input
            type="text"
            placeholder="Search Users"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="header-search-input"
          />
        </div>

        {/* Filter Controls Group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          {/* Active/Inactive Toggle */}
          <FilterIconButton
            active={!showInactive}
            onClick={() => {
              const newValue = !showInactive;
              setShowInactive(newValue);
              updateFilterPref('usersShowInactive', newValue);
            }}
            tooltip={showInactive ? "Hide inactive users" : "Show inactive users"}
          >
            {showInactive ? <Eye size={18} /> : <EyeOff size={18} />}
          </FilterIconButton>

          {/* Role Filter */}
          <Select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              updateFilterPref('usersRoleFilter', e.target.value);
            }}
            size="small"
            displayEmpty
            sx={{ 
              minWidth: 110, 
              fontSize: 'var(--text-xs)',
              '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }
            }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ShieldCheck size={14} style={{ opacity: value !== 'all' ? 1 : 0.5 }} />
                <span>{value === 'all' ? 'All Roles' : (ROLE_LABELS[value] || value)}</span>
              </Box>
            )}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {ROLE_HIERARCHY.map((role) => (
              <MenuItem key={role} value={role}>{ROLE_LABELS[role]}</MenuItem>
            ))}
          </Select>

          {/* Billing Filter */}
          {billingPolicy.enabled && <Select
            value={billingFilter}
            onChange={(e) => {
              setBillingFilter(e.target.value);
              updateFilterPref('usersBillingFilter', e.target.value);
            }}
            size="small"
            displayEmpty
            sx={{ 
              minWidth: 110, 
              fontSize: 'var(--text-xs)',
              '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }
            }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CreditCard size={14} style={{ opacity: value !== 'all' ? 1 : 0.5 }} />
                <span>{value === 'all' ? 'All Billing' : value === 'none' ? 'No Billing' : value === 'past_due' ? 'Past Due' : value.charAt(0).toUpperCase() + value.slice(1)}</span>
              </Box>
            )}
          >
            <MenuItem value="all">All Billing</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="trialing">Trialing</MenuItem>
            <MenuItem value="past_due">Past Due</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
            <MenuItem value="none">No Billing</MenuItem>
          </Select>}

          {/* Company Filter */}
          <SearchableAdminSelect
            label="Company"
            value={companyFilter}
            onChange={selectedId => {
              setCompanyFilter(selectedId);
              updateFilterPref('usersCompanyFilter', selectedId);
            }}
            clearValue="all"
            sx={{ width: 220, minWidth: 185 }}
            options={[
              { value: 'all', label: 'All Companies' },
              { value: 'none', label: 'No Company' },
              ...companies.map(company => ({ value: company.id, label: company.name || company.code || company.id })),
            ]}
          />

          {/* Unverified Only Toggle */}
          <FilterIconButton
            active={showUnverifiedOnly}
            onClick={() => {
              const newValue = !showUnverifiedOnly;
              setShowUnverifiedOnly(newValue);
              updateFilterPref('usersShowUnverifiedOnly', newValue);
            }}
            tooltip={showUnverifiedOnly ? "Show all users" : "Show unverified emails only"}
            color="var(--color-warning)"
          >
            <MailX size={18} />
          </FilterIconButton>

          {/* Login Issues Toggle */}
          <FilterIconButton
            active={showLoginIssuesOnly}
            onClick={() => {
              const newValue = !showLoginIssuesOnly;
              setShowLoginIssuesOnly(newValue);
              updateFilterPref('usersShowLoginIssuesOnly', newValue);
            }}
            tooltip={showLoginIssuesOnly ? "Show all users" : `Show users with login failures (${Object.keys(loginIssues).length})`}
            color="var(--color-error-text)"
          >
            <AlertTriangle size={18} />
          </FilterIconButton>

          {/* Clear Filters Button */}
          {activeFilterCount > 0 && (
            <Tooltip title="Clear all filters" arrow>
              <Chip
                icon={<X size={14} />}
                label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
                size="small"
                onClick={clearAllFilters}
                onDelete={clearAllFilters}
                color="primary"
                variant="outlined"
                sx={{ ml: 0.5 }}
              />
            </Tooltip>
          )}

          {/* Invite User Button */}
          <Tooltip title="Send a user invitation" arrow>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UserPlus size={14} />}
              onClick={() => setIsInviteModalOpen(true)}
              sx={{ ml: 1, px: 1.5, height: 32, flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Invite User
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}
      
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              <TableCell sx={{ fontWeight: 600 }} sortDirection={sortConfig.key === 'displayName' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'displayName'}
                  direction={sortConfig.key === 'displayName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('displayName')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} sortDirection={sortConfig.key === 'email' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'email'}
                  direction={sortConfig.key === 'email' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} sortDirection={sortConfig.key === 'userRole' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'userRole'}
                  direction={sortConfig.key === 'userRole' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('userRole')}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} sortDirection={sortConfig.key === 'lastLoginTime' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'lastLoginTime'}
                  direction={sortConfig.key === 'lastLoginTime' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('lastLoginTime')}
                >
                  Last Login
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center" sortDirection={sortConfig.key === 'active' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'active'}
                  direction={sortConfig.key === 'active' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('active')}
                >
                  Active
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">AI</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {searchQuery || activeFilterCount > 0 
                    ? "No users match your current filters" 
                    : "No users found"}
                </TableCell>
              </TableRow>
            ) : (
              currentUsers.map((user) => {
                const aiAccess = getUserAiAccess(user);
                const aiPresentation = getAccessPresentation(aiAccess);
                const documentActivity = userDocumentActivity?.[user.id] || {
                  documentsCreated: 0,
                  patientsDocumented: 0,
                  lastDocumentAt: null,
                };
                const lastDocumentDate = documentActivity.lastDocumentAt
                  ? new Date(documentActivity.lastDocumentAt)
                  : null;
                const activityLabel = documentActivity.documentsCreated > 0
                  ? `${documentActivity.documentsCreated} document${documentActivity.documentsCreated === 1 ? '' : 's'} created in the last 7 days; ${documentActivity.patientsDocumented} patient${documentActivity.patientsDocumented === 1 ? '' : 's'} documented${lastDocumentDate ? `; last document ${lastDocumentDate.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}`
                  : 'No documents created in the last 7 days';
                return (
                <TableRow
                  key={user.id}
                  sx={{
                    opacity: user.disabled ? 0.5 : 1,
                    backgroundColor: user.id === userId ? 'var(--color-highlight-light)' : 'inherit'
                  }}
                >
                  <TableCell>
                    <span 
                      className="table-primary-text"
                      style={{ cursor: 'pointer', color: 'var(--color-primary)' }}
                      onClick={() => handleOpenSettingsDialog(user)}
                    >
                      {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unnamed'}
                    </span>
                    {userDocumentActivity && (
                      <Tooltip
                        title={(
                          <Box sx={{ py: 0.25 }}>
                            <Box sx={{ fontWeight: 600, mb: 0.5 }}>Last 7 days</Box>
                            <Box>Documents created: {documentActivity.documentsCreated}</Box>
                            <Box>Patients documented: {documentActivity.patientsDocumented}</Box>
                            {lastDocumentDate && (
                              <Box>
                                Last document: {lastDocumentDate.toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </Box>
                            )}
                          </Box>
                        )}
                        arrow
                      >
                        <Box
                          component="span"
                          role="img"
                          tabIndex={0}
                          aria-label={activityLabel}
                          sx={{
                            display: 'inline-flex',
                            verticalAlign: 'middle',
                            ml: 0.75,
                            color: documentActivity.documentsCreated > 0
                              ? 'var(--color-primary)'
                              : 'var(--color-text-secondary)',
                            opacity: documentActivity.documentsCreated > 0 ? 0.9 : 0.4,
                            cursor: 'help',
                          }}
                        >
                          <FileText size={14} aria-hidden="true" />
                        </Box>
                      </Tooltip>
                    )}
                    {user.id === userId && (
                      <span style={{ 
                        fontSize: 'var(--text-xs)', 
                        marginLeft: '8px', 
                        color: 'var(--color-primary)',
                        fontWeight: 500 
                      }}>
                        (You)
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Click to copy email" arrow>
                      <span 
                        className="patient-details-small"
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Copy email for ${user.displayName || user.email}`}
                        onClick={() => copyValue(user.email, 'Email')}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            copyValue(user.email, 'Email');
                          }
                        }}
                      >
                        {user.email}
                        <Copy size={12} style={{ opacity: 0.6 }} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={user.userRole || "standard"}
                      onChange={(e) => handleRoleChange(user, e.target.value)}
                      size="small"
                      disabled={updating[user.id] || (user.id === userId)}
                      sx={{ minWidth: 140, fontSize: 'var(--text-sm)' }}
                    >
                      {ROLE_HIERARCHY.map((role) => (
                        <MenuItem key={role} value={role}>{ROLE_LABELS[role]}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <span className="patient-details-small">
                      {formatDate(user.lastLoginTime)}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={user.id === userId ? "Cannot deactivate yourself" : (user.disabled ? "Activate user" : "Deactivate user")} arrow>
                      <span>
                        <Switch
                          checked={!user.disabled}
                          onChange={() => handleActiveToggle(user)}
                          disabled={updating[user.id] || user.id === userId}
                          color="primary"
                          size="small"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={aiPresentation.message} arrow>
                      <Chip
                        label={aiPresentation.label}
                        color={aiPresentation.color}
                        variant={aiAccess.enabled ? 'filled' : 'outlined'}
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      {loginIssues[user.email] && (() => {
                        const issue = loginIssues[user.email];
                        const lines = [
                          `${issue.count} failed login attempt${issue.count > 1 ? 's' : ''} in last 7 days`,
                          issue.lastFailure ? `Last: ${issue.lastFailure.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}` : null,
                          issue.hasLockout ? '⚠ Account lockout detected (too many attempts)' : null
                        ].filter(Boolean).join('\n');
                        return (
                          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{lines}</span>} arrow>
                            <IconButton
                              size="small"
                              sx={{ 
                                color: issue.hasLockout ? 'var(--color-error-text)' : 'var(--color-warning-text)',
                                cursor: 'default',
                                '&:hover': { backgroundColor: issue.hasLockout ? 'var(--color-error-light)' : 'var(--color-warning-light)' }
                              }}
                            >
                              <AlertTriangle size={16} />
                            </IconButton>
                          </Tooltip>
                        );
                      })()}
                      {user.emailVerified === false && (
                        <Tooltip title="Manually verify this user's email" arrow>
                          <IconButton 
                            size="small" 
                            onClick={() => handleManualVerification(user)}
                            disabled={updating[user.id]}
                            sx={{ color: 'var(--color-success-text)', '&:hover': { backgroundColor: 'var(--color-success-light)' } }}
                          >
                            <MailCheck size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View feedback in Userback" arrow>
                        <IconButton
                          size="small"
                          onClick={() => window.open(`https://app.userback.io/feedback?search=${encodeURIComponent(user.email)}`, '_blank')}
                          sx={{ color: 'var(--color-info-text)', '&:hover': { backgroundColor: 'var(--color-info-light)' } }}
                        >
                          <ExternalLink size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit user settings" arrow>
                        <IconButton size="small" onClick={() => handleOpenSettingsDialog(user)}>
                          <Edit size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.id === userId ? "Cannot delete yourself" : "Delete user (Firestore only)"} arrow>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDeleteDialog(user)}
                            disabled={user.id === userId}
                            sx={{ color: 'var(--color-error-text)', '&:hover': { backgroundColor: 'var(--color-error-light)' } }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            Showing {filteredUsers.length > 0 ? indexOfFirstUser + 1 : 0}-{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
            {(activeFilterCount > 0 || searchQuery) && ` (filtered from ${users.length} total)`}
          </span>
        </Box>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            variant="outlined"
            shape="rounded"
            size="small"
          />
        )}
      </div>

      {/* User Settings Dialog */}
      <Dialog 
        open={settingsDialogOpen} 
        onClose={() => handleCloseSettingsDialog()}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      >
        <DialogTitle>User Settings</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mt: 1, mb: 2 }}>{saveError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={editingUser?.email || ''}
              fullWidth
              disabled
              helperText="Email cannot be changed"
            />
            <Box>
              <SearchableAdminSelect
                label="Company"
                value={userFormData.companyId}
                onChange={selectedId => setUserFormData(prev => ({ ...prev, companyId: selectedId }))}
                options={[
                  { value: '', label: 'No company assigned' },
                  ...companies.map(company => ({ value: company.id, label: company.name || company.code || company.id })),
                ]}
              />
            </Box>
            <TextField
              label="Display Name"
              value={userFormData.displayName}
              onChange={(e) => setUserFormData(prev => ({ ...prev, displayName: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={userFormData.excludeFromAuditLogs}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, excludeFromAuditLogs: e.target.checked }))}
                  color="warning"
                />
              }
              label="Exclude from statistics and audit logs"
            />
            <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', mt: -1 }}>
              Enable this for test accounts to exclude their activity from admin statistics and audit logs.
            </Box>
            <TextField
              label="Invite Cap Override"
              value={userFormData.inviteCap}
              onChange={(e) => setUserFormData(prev => ({ ...prev, inviteCap: e.target.value.replace(/[^0-9]/g, '') }))}
              fullWidth
              type="text"
              helperText={`Leave blank for role default (${getEffectiveInviteCap({ userRole: editingUser?.userRole })}). Set to 0 to disable invites.`}
              placeholder="Role default"
            />
            {/* Read-only invite & billing summary */}
            <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: 'var(--color-background-alt)', border: '1px solid var(--color-border)' }}>
                <Box sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', mb: 0.5 }}>Invites Sent</Box>
                <Box sx={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                  {editingUser?.invitesSent || 0} / {getEffectiveInviteCap(editingUser || {})}
                </Box>
              </Box>
              {billingPolicy.enabled && <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: 'var(--color-background-alt)', border: '1px solid var(--color-border)' }}>
                <Box sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', mb: 0.5 }}>Billing</Box>
                <Box sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, ...getSubscriptionStatusStyle(editingUser?.subscriptionStatus) }}>
                  {editingUser?.stripeCustomerId
                    ? `${formatPlan(editingUser)} · ${formatSubscriptionStatus(editingUser)}`
                    : '—'}
                </Box>
              </Box>}
            </Box>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 1 }}>Feature Overrides</Box>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={userFormData.useAiAssistant === true}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, useAiAssistant: e.target.checked }))}
                  />
                }
                label="AI Assistant"
              />
              <Alert severity={editingAiPresentation.severity} sx={{ mt: 0.5 }}>
                {editingAiPresentation.message}
                {userFormData.useAiAssistant === null && (
                  <> Enable the switch to repair the missing legacy value; leaving it untouched preserves the record.</>
                )}
              </Alert>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseSettingsDialog()}>Cancel</Button>
          <Button 
            onClick={handleSaveUserSettings} 
            variant="contained" 
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        setIsOpen={setIsInviteModalOpen}
        userData={users.find(u => u.id === userId) || { userRole: 'superAdmin' }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          ⚠️ Delete User Permanently
        </DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>THIS ACTION CANNOT BE UNDONE.</strong>
          </Alert>
          
          <Box sx={{ mb: 2 }}>
            <p style={{ margin: '0 0 12px 0' }}>
              You are about to <strong>permanently delete</strong> the Firestore record for:
            </p>
            <Box sx={{ 
              backgroundColor: 'var(--color-background-alt)', 
              p: 2, 
              borderRadius: 1,
              border: '1px solid var(--color-border)',
              mb: 2
            }}>
              <strong style={{ fontSize: 'var(--text-lg)' }}>
                {userToDelete?.displayName || `${userToDelete?.firstName || ''} ${userToDelete?.lastName || ''}`.trim() || 'Unnamed'}
              </strong>
              <br />
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {userToDelete?.email}
              </span>
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This only deletes the Firestore user document. 
            The Firebase Authentication record will remain and must be deleted separately from the Firebase Console.
            The user will not be able to log in, but their auth credentials still exist.
          </Alert>

          <p style={{ margin: '0 0 8px 0' }}>
            To confirm deletion, type the user's email exactly as shown above:
          </p>
          <TextField
            fullWidth
            placeholder={userToDelete?.email || ''}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={isDeleting}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== userToDelete?.email}
            helperText={deleteConfirmText.length > 0 && deleteConfirmText !== userToDelete?.email ? 'Email does not match' : ''}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUser}
            variant="contained" 
            color="error"
            disabled={isDeleting || deleteConfirmText !== userToDelete?.email}
          >
            {isDeleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
